import {
  Action,
  ActionGroup,
  Button,
  Checkbox,
  CheckboxButton,
  CheckboxGroup,
  ColumnLayout,
  Content,
  FieldDescription,
  Header,
  Heading,
  Icon,
  IconApp,
  IllustratedMessage,
  InlineCode,
  Label,
  Modal,
  Option,
  RadioButton,
  RadioGroup,
  Section,
  Select,
  Text,
  TextField,
  useOverlayController,
} from "@mittwald/flow-remote-react-components";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  Form,
  typedField,
} from "@mittwald/flow-remote-react-components/react-hook-form";
import { useEffect, useState } from "react";
import { CreateUserError } from "../../errors/CreateUserError";
import { useListUsers } from "../../hooks/useListUsers";
import CreateUserModal from "../User/CreateUserModal";
import { useDeclareDirectoryProtection } from "../../hooks/useDeclareDirectoryProtection";
import { useListAppInstallations } from "../../hooks/useListAppInstallations";
import { NoUsers } from "../User/NoUsers";
import { useListDirectoryProtections } from "../../hooks/useListDirectoryProtections.ts";

interface FormInputs {
  description: string;
  users: string[];
  path: string;
  appInstallationId: string;
}

export const CreateDirectoryProtectionModal = (props: {
  projectId: string;
}) => {
  const [errorOnSubmit, setErrorOnSubmit] = useState<Error | undefined>(
    undefined,
  );
  const [type, setType] = useState<"directory" | "path">("directory");
  const [protectWpLogin, setProtectWpLogin] = useState(true);

  if (errorOnSubmit) {
    throw errorOnSubmit;
  }

  const controller = useOverlayController("Modal");
  // The user modal is rendered next to the form, not inside it (see
  // CreateUserModal), so it needs its own controller.
  const createUserModalController = useOverlayController("Modal", {
    reuseControllerFromContext: false,
  });

  const users = useListUsers(props.projectId);
  const directoryProtections = useListDirectoryProtections(props.projectId);

  const form = useForm<FormInputs>({
    defaultValues: {
      description: "",
      users: [],
      path: "",
      appInstallationId: "",
    },
  });

  // Registered in an effect so the handler is added once and removed on
  // unmount (addOnClose returns the disposer).
  useEffect(
    () =>
      controller.addOnClose(() => {
        form.reset();
        setProtectWpLogin(true);
      }),
    [controller, form],
  );

  const wpLoginAlreadyProtected = directoryProtections.data.some(
    (dp) =>
      dp.appInstallationId === form.watch("appInstallationId") &&
      dp.path === "/wp-login.php",
  );

  const declareDirectoryProtectionMutation = useDeclareDirectoryProtection(
    props.projectId,
  );

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const result = await declareDirectoryProtectionMutation.mutateAsync({
      description: data.description,
      path: data.path,
      users: data.users,
      appInstallationId: data.appInstallationId,
    });

    if (result.error) {
      setErrorOnSubmit(new CreateUserError(result.error.message));
      return;
    }

    if (
      data.path === "/wp-admin" &&
      protectWpLogin &&
      !wpLoginAlreadyProtected
    ) {
      const wpLoginResult =
        await declareDirectoryProtectionMutation.mutateAsync({
          description: data.description,
          path: "/wp-login.php",
          users: data.users,
          appInstallationId: data.appInstallationId,
        });

      if (wpLoginResult.error) {
        setErrorOnSubmit(new CreateUserError(wpLoginResult.error.message));
        return;
      }
    }

    controller.close();
  };

  const appInstallations = useListAppInstallations(props.projectId);

  const directories =
    appInstallations.data.find((a) => a.id == form.watch().appInstallationId)
      ?.directories ?? [];

  // Another app has other directories: start the path and the wp-login option
  // over whenever the user picks a different app.
  useEffect(
    () =>
      form.subscribe({
        name: "appInstallationId",
        formState: { values: true },
        callback: ({ type }) => {
          if (type !== "change") {
            return;
          }
          form.resetField("path");
          setProtectWpLogin(true);
        },
      }),
    [form],
  );

  const Field = typedField(form);

  const pathValidation = {
    required: "Bitte wähle aus, welches Verzeichnis geschützt werden soll",
    validate: {
      noDuplicate: (_value: string[] | string, values: FormInputs) => {
        return directoryProtections.data.find(
          (dp) =>
            dp.appInstallationId == values.appInstallationId &&
            dp.path == values.path,
        )
          ? "Dieses Verzeichnis wird schon geschützt"
          : true;
      },
      startsWithSlash: (value: string | string[]) => {
        if (Array.isArray(value)) {
          throw new Error("This should not be an array");
        }

        if (!value.startsWith("/")) {
          return 'Der Pfad muss mit einem "/" beginnen';
        }

        return true;
      },
    },
  };

  if (appInstallations.data.length === 0) {
    return (
      <Modal size="m" offCanvas>
        <Heading>
          <Text>Passwortschutz anlegen</Text>
        </Heading>
        <Content>
          <IllustratedMessage>
            <Icon>
              <IconApp />
            </Icon>
            <Heading>Noch keine App installiert</Heading>
            <Text>
              In diesem Projekt ist noch keine App installiert. Installiere
              zuerst eine App, um für sie einen Passwortschutz anlegen zu
              können.
            </Text>
          </IllustratedMessage>
        </Content>
        <ActionGroup>
          <Action
            onAction={() => {
              controller.close();
            }}
          >
            <Button color="secondary" variant="soft">
              Schließen
            </Button>
          </Action>
        </ActionGroup>
      </Modal>
    );
  }

  return (
    <Modal size="m" offCanvas>
      <Form form={form} onSubmit={onSubmit}>
        <Heading>
          <Text>Passwortschutz anlegen</Text>
        </Heading>
        <Content>
          <Section>
            <Heading>Beschreibung festlegen</Heading>
            <Text>
              Gib deinem Passwortschutz einen schlüssigen Namen. Dieser wird
              später in der Übersicht und beim Aufruf des geschützten Bereichs
              angezeigt.
            </Text>
            <ColumnLayout m={[1, 1]}>
              <Field
                name="description"
                rules={{
                  required: "Bitte gib eine passende Beschreibung an",
                }}
              >
                <TextField isRequired autoFocus>
                  <Label>Name des Passwortschutzes</Label>
                </TextField>
              </Field>
            </ColumnLayout>
          </Section>
          <Section>
            <Heading>App auswählen</Heading>
            <Text>
              Wähle aus, für welche App dieser Passwortschutz angelegt werden
              soll.
            </Text>
            <ColumnLayout m={[1, 1]}>
              <Field
                name="appInstallationId"
                rules={{
                  required: "Bitte wähle eine App aus",
                }}
              >
                <Select isRequired placeholder="App auswählen">
                  <Label>App auswählen</Label>
                  {appInstallations.data.map((appInstallation) => (
                    <Option value={appInstallation.id} key={appInstallation.id}>
                      {appInstallation.description}
                    </Option>
                  ))}
                </Select>
              </Field>
              <Field
                name="path"
                rules={{
                  required:
                    "Bitte wähle aus, welches Verzeichnis geschützt werden soll",
                  validate: {
                    noDuplicate: (_value, values) => {
                      return directoryProtections.data.find(
                        (dp) =>
                          dp.appInstallationId == values.appInstallationId &&
                          dp.path == values.path,
                      )
                        ? "Dieses Verzeichnis wird schon geschützt"
                        : true;
                    },
                  },
                }}
              ></Field>
            </ColumnLayout>
          </Section>
          <Section>
            <Heading>Verzeichnis bzw. Pfad auswählen</Heading>
            <Text>
              Wähle das Verzeichnis oder einen benutzerdefinierten Pfad aus,
              welchen du vor unberechtigtem Zugriff schützen möchtest.
            </Text>
            <RadioGroup
              value={type}
              onChange={(value: string) => {
                if (value == "directory" || value == "path") {
                  setType(value);

                  if (
                    value == "directory" &&
                    !directories.includes(form.watch().path)
                  ) {
                    form.resetField("path");
                  }

                  if (value == "path") {
                    if (form.watch().path == "") {
                      form.setValue("path", "/");
                    }
                  }

                  return;
                }

                throw new Error("invalid type");
              }}
            >
              <RadioButton value="directory">
                Vorgeschlagenes Verzeichnis
              </RadioButton>
              <RadioButton value="path">Benutzerdefinierter Pfad</RadioButton>

              {form.watch().appInstallationId == "" && (
                <FieldDescription>
                  Wähle eine App aus, die du schützen möchtest.
                </FieldDescription>
              )}
            </RadioGroup>
            {type == "directory" && (
              <Field name="path" rules={pathValidation}>
                <Select
                  // Remount when the app changes: the option set is swapped and
                  // the previous selectedKey may not exist in the new one, which
                  // freezes the Select (see mittwald/flow#2977 and the Select docs).
                  key={form.watch().appInstallationId}
                  isRequired
                  isDisabled={
                    form.watch().appInstallationId == "" ||
                    directories.length == 0
                  }
                  placeholder={
                    directories.length == 0
                      ? "keine Verzeichnisse gefunden"
                      : "Verzeichnis auswählen"
                  }
                >
                  <Label>Unterverzeichnis auswählen</Label>
                  <FieldDescription>
                    Für andere Pfade wähle &quot;Benutzerdefinierter Pfad&quot;
                  </FieldDescription>
                  {directories.map((d) => (
                    <Option key={d} value={d}>
                      {d}
                    </Option>
                  ))}
                </Select>
              </Field>
            )}
            {type == "path" && (
              <Field name="path" rules={pathValidation}>
                <TextField
                  isRequired
                  autoFocus
                  isDisabled={form.watch().appInstallationId == ""}
                >
                  <Label>Pfad</Label>
                  <FieldDescription>
                    z. B. <InlineCode>/admin</InlineCode> oder{" "}
                    <InlineCode>/wp-admin</InlineCode>
                  </FieldDescription>
                </TextField>
              </Field>
            )}
          </Section>
          {form.watch("path") === "/wp-admin" && (
            <Section>
              <Checkbox
                isSelected={protectWpLogin && !wpLoginAlreadyProtected}
                onChange={(selected) => {
                  setProtectWpLogin(selected);
                }}
                isDisabled={wpLoginAlreadyProtected}
              >
                wp-login.php ebenfalls schützen
              </Checkbox>
              <Text>
                Die Datei wp-login.php liegt außerhalb von /wp-admin und ist
                ohne zusätzlichen Schutz weiterhin direkt erreichbar.
              </Text>
            </Section>
          )}
          <Section>
            <Header>
              <Heading>Benutzer hinzufügen</Heading>
              <Button
                color="secondary"
                onPress={() => {
                  createUserModalController.open();
                }}
              >
                Neuen Benutzer anlegen
              </Button>
            </Header>
            <Text>
              Dein Passwortschutz kann für einen bzw. mehrere Benutzer
              freigeschaltet werden.
            </Text>

            {users.data.length > 0 ? (
              <Field
                name="users"
                rules={{
                  required: "Bitte wähle mindestens einen Benutzer aus",
                }}
              >
                <CheckboxGroup isRequired>
                  <Label>Benutzer auswählen</Label>
                  {users.data.map((u) => (
                    <CheckboxButton key={u.username} value={u.username}>
                      {u.username}
                    </CheckboxButton>
                  ))}
                </CheckboxGroup>
              </Field>
            ) : (
              <NoUsers
                projectId={props.projectId}
                onCreateUser={() => {
                  createUserModalController.open();
                }}
              />
            )}
          </Section>
        </Content>
        <ActionGroup>
          <Button
            color="success"
            type="submit"
            isPending={declareDirectoryProtectionMutation.isPending}
            isSucceeded={declareDirectoryProtectionMutation.isSuccess}
          >
            Passwortschutz anlegen
          </Button>
          <Action
            onAction={() => {
              controller.close();
            }}
          >
            <Button color="secondary" variant="soft">
              Abbrechen
            </Button>
          </Action>
        </ActionGroup>
      </Form>
      <CreateUserModal
        projectId={props.projectId}
        controller={createUserModalController}
      />
    </Modal>
  );
};
